import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';

export type UseRoutesMatchResult<TRoutes extends Record<string, string | string[]>> = {
  readonly matchedRouteName: keyof TRoutes | undefined;
  readonly routeNames: { [x in keyof TRoutes]: x };
};

export type UseRoutesMatchOptions = {
  readonly exact: boolean;
};

export function useRoutesMatch<TRoutes extends Record<string, string | string[]>>(
  routeDefinitions: TRoutes,
  { exact }: UseRoutesMatchOptions = { exact: false }
): UseRoutesMatchResult<TRoutes> {
  const routeDefinitionKeys = Object.keys(routeDefinitions);
  const location = useLocation();

  const matchedRouteName = React.useMemo(() => {
    const allPaths = Object.values(routeDefinitions).flat();

    for (const path of allPaths) {
      const match = matchPath({ path, end: exact }, location.pathname);

      if (match) {
        return routeDefinitionKeys.find(key =>
          ([] as string[]).concat(routeDefinitions[key]).includes(path)
        );
      }
    }
    return routeDefinitionKeys[0];
  }, [routeDefinitionKeys, routeDefinitions, location.pathname, exact]);

  const routeNames = React.useMemo(
    () =>
      routeDefinitionKeys.reduce((result, nextKey) => {
        return {
          ...result,
          [nextKey]: nextKey,
        };
      }, {}) as { [p in keyof TRoutes]: p },
    [routeDefinitionKeys]
  );

  return {
    matchedRouteName,
    routeNames,
  };
}
